import * as luxon from 'luxon';
import type { ProgramQuote, RetainedProgramDisplay } from '@makemydeal/dr-dash-types';
import type { OfferType, PaymentTerm } from '@makemydeal/dr-platform-types';
import * as programPayloadUtil from '@makemydeal/dr-dash-store/utils/programUtils';
import { CREDIT_TIER_OPTIONS } from './creditTiers';

export const isProgramNotExpired = (quote: any): boolean => {
    // We want to include quotes that do not have an end date;
    const endDate = quote?.lenderProgram?.programEndDate;

    if (!endDate) {
        return true;
    }

    const today = luxon.DateTime.now();
    const expiredDate = luxon.DateTime.fromISO(endDate);
    // We want to exclude quotes that are older than 7 days, giving leeway if the expiration is up to midnight the current day
    const isExpired = expiredDate.diff(today, 'days').days <= -8;
    return !isExpired;
};

const buildRowKey = (item: ProgramQuote, index: number): string => {
    const contextId = programPayloadUtil.buildProgramContextId(item);
    if (!contextId) return `idx-${index}`;
    return [contextId, item.requestedTier ?? '', item.buyRate ?? ''].join('|');
};

export const createProgramTableData = (data: ProgramQuote[], useContentKey = false) => {
    return data.map((item, index) => ({
        ...item,
        key: useContentKey ? buildRowKey(item, index) : index.toString()
    }));
};

export const pickByRequestedTier = <T extends { requestedTier?: string }>(rows: T[], tier?: string): T | undefined => {
    if (tier != null) {
        const tierMatch = rows.find((row) => row.requestedTier === tier);
        if (tierMatch) return tierMatch;
    }
    return rows[0];
};

export const findAppliedProgramKey = (
    data: Array<ProgramQuote & { key: string }>,
    selectedTerm: PaymentTerm,
    offerType: OfferType,
    appliedProgram?: ProgramQuote,
    retained?: RetainedProgramDisplay,
    dealTier?: string
): string => {
    const strictMatch = pickByRequestedTier(
        data.filter((value) => programPayloadUtil.isAppliedRowDisplay(selectedTerm, value, offerType, appliedProgram, retained)),
        dealTier
    );
    if (strictMatch) return strictMatch.key;
    const looseMatch = pickByRequestedTier(
        data.filter((value) => programPayloadUtil.isAppliedRowDisplay(selectedTerm, value, offerType, undefined, retained)),
        dealTier
    );
    if (looseMatch) return looseMatch.key;
    const identityMatches = data.filter((value) => programPayloadUtil.matchesProgramIdentityForTerm(selectedTerm, value, retained));
    if (dealTier != null) {
        const dealMatch = identityMatches.find((match) => match.requestedTier === dealTier);
        if (dealMatch) return dealMatch.key;
    }
    return identityMatches.length === 1 ? identityMatches[0].key : '';
};

export const getRowClassName = (record: ProgramQuote & { key?: string }, appliedProgramKey: string): string => {
    return record.key !== undefined && record.key === appliedProgramKey ? 'selected-row' : '';
};

export const findExcludingIncentiveId = (
    incentiveId: number,
    incentiveExclusionsById: Record<number, number[]>,
    selectedIncentiveIds: number[]
): number | undefined => {
    const match = Object.entries(incentiveExclusionsById).find(
        ([excluderId, excluded]) => selectedIncentiveIds.includes(Number(excluderId)) && excluded.includes(incentiveId)
    );
    return match === undefined ? undefined : Number(match[0]);
};

export const shouldExcludeIncentive = (
    incentiveId: number,
    incentiveExclusionsById: Record<number, number[]>,
    selectedIncentiveIds?: number[]
): boolean => {
    if (selectedIncentiveIds !== undefined) {
        return findExcludingIncentiveId(incentiveId, incentiveExclusionsById, selectedIncentiveIds) !== undefined;
    }
    return Object.values(incentiveExclusionsById).some((exclusions: number[]) => exclusions.includes(incentiveId));
};

const FROZEN_REGION_LAST_CELL_SELECTOR = '.ant-table-thead .ant-table-cell-fix-left-last';
const FROZEN_REGION_CELLS_SELECTOR = '.ant-table-thead .ant-table-cell-fix-left';

export const findFrozenRegionCell = (tableWrapper: HTMLElement | null): HTMLElement | null => {
    const cell = tableWrapper?.querySelector(FROZEN_REGION_LAST_CELL_SELECTOR);
    return cell instanceof HTMLElement ? cell : null;
};

export const findFrozenRegionCells = (tableWrapper: HTMLElement | null): HTMLElement[] =>
    Array.from(tableWrapper?.querySelectorAll(FROZEN_REGION_CELLS_SELECTOR) ?? []).filter(
        (cell): cell is HTMLElement => cell instanceof HTMLElement
    );

const EXPANDED_REGION_SELECTOR = '.ant-table-expanded-row-fixed';

const findExpandedRegion = (tableWrapper: HTMLElement | null): HTMLElement | undefined =>
    Array.from(tableWrapper?.querySelectorAll(EXPANDED_REGION_SELECTOR) ?? []).find(
        (region): region is HTMLElement => region instanceof HTMLElement && region.getBoundingClientRect().width !== 0
    );

export const measureFrozenRegionOffset = (tableWrapper: HTMLElement | null): string | undefined => {
    const lastFrozenCell = findFrozenRegionCell(tableWrapper);
    const expandedRegion = findExpandedRegion(tableWrapper);
    if (!lastFrozenCell || !expandedRegion) {
        return undefined;
    }
    const offset = Math.round(lastFrozenCell.getBoundingClientRect().right - expandedRegion.getBoundingClientRect().left);
    return offset > 0 ? `${offset}px` : undefined;
};

const parseAmount = (value?: string): number => {
    const parsed = Number((value ?? '').replace(/[^0-9.-]/g, ''));
    return Number.isFinite(parsed) && value ? parsed : Number.POSITIVE_INFINITY;
};

const TIER_ORDER: string[] = CREDIT_TIER_OPTIONS.map((option): string => option.value);

const getTierRank = (tier: string | undefined, pinnedIndex: number): number => {
    const tierIndex = TIER_ORDER.indexOf(tier ?? '');
    if (tierIndex < 0) return TIER_ORDER.length;
    if (pinnedIndex < 0) return tierIndex;
    if (tierIndex === pinnedIndex) return 0;
    return tierIndex < pinnedIndex ? tierIndex + 1 : tierIndex;
};

export const sortByDealDefaults = <T extends ProgramQuote>(rows: T[], pinnedTier?: string, pinnedTerm?: number): T[] => {
    const pinnedIndex = TIER_ORDER.indexOf(pinnedTier ?? '');
    const hasPinnedTier = pinnedIndex >= 0;
    return [...rows].sort((first, second) => {
        const firstTierRank = getTierRank(first.requestedTier, pinnedIndex);
        const tierDiff = firstTierRank - getTierRank(second.requestedTier, pinnedIndex);
        if (tierDiff !== 0) return tierDiff;
        const firstTerm = first.term ?? 0;
        const secondTerm = second.term ?? 0;
        if (firstTerm !== secondTerm) {
            if (hasPinnedTier && firstTierRank === 0 && pinnedTerm !== undefined) {
                if (firstTerm === pinnedTerm) return -1;
                if (secondTerm === pinnedTerm) return 1;
            }
            return secondTerm - firstTerm;
        }
        const firstPayment = parseAmount(first.monthlyPayment);
        const secondPayment = parseAmount(second.monthlyPayment);
        if (firstPayment === secondPayment) return 0;
        return firstPayment < secondPayment ? -1 : 1;
    });
};
